import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { useWhatsAppPopup } from '@/contexts/WhatsAppPopupContext';

// --- Radar Chart Data ---
interface RadarAxis {
    label: string;
    specialist: number;
    generalist: number;
}

const axes: RadarAxis[] = [
    { label: 'Velocidade\nBurocrática', specialist: 95, generalist: 60 },
    { label: 'Prevenção\nde Multas', specialist: 90, generalist: 50 },
    { label: 'Economia\nde Tempo', specialist: 85, generalist: 70 },
    { label: 'Conhecimento\nda Capitania', specialist: 98, generalist: 55 },
    { label: 'Tranquilidade', specialist: 90, generalist: 40 },
];

// --- SVG Radar Chart ---
const RadarChart: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
    const cx = 150;
    const cy = 150;
    const maxRadius = 110;
    const rings = [0.25, 0.5, 0.75, 1.0];
    const n = axes.length;
    const angleStep = (2 * Math.PI) / n;

    const getPoint = (index: number, value: number) => {
        const angle = (index * angleStep) - Math.PI / 2;
        const r = (value / 100) * maxRadius;
        return {
            x: cx + r * Math.cos(angle),
            y: cy + r * Math.sin(angle),
        };
    };

    const getPolygonPoints = (values: number[]) =>
        values.map((v, i) => {
            const p = getPoint(i, v);
            return `${p.x},${p.y}`;
        }).join(' ');

    const specialistPoints = getPolygonPoints(axes.map(a => a.specialist));
    const generalistPoints = getPolygonPoints(axes.map(a => a.generalist));

    return (
        <div className="flex flex-col items-center">
            <svg viewBox="0 0 300 300" className="w-full max-w-[380px]">
                {/* Background rings */}
                {rings.map((ring) => (
                    <polygon
                        key={ring}
                        points={Array.from({ length: n }, (_, i) => {
                            const p = getPoint(i, ring * 100);
                            return `${p.x},${p.y}`;
                        }).join(' ')}
                        fill="none"
                        stroke="hsl(222, 47%, 25%)"
                        strokeWidth="0.5"
                        opacity="0.15"
                    />
                ))}

                {/* Axis lines */}
                {axes.map((_, i) => {
                    const p = getPoint(i, 100);
                    return (
                        <line
                            key={i}
                            x1={cx} y1={cy}
                            x2={p.x} y2={p.y}
                            stroke="hsl(222, 47%, 25%)"
                            strokeWidth="0.5"
                            opacity="0.15"
                        />
                    );
                })}

                {/* Generalist polygon */}
                <motion.polygon
                    points={generalistPoints}
                    fill="hsl(222, 47%, 45%)"
                    fillOpacity="0.15"
                    stroke="hsl(222, 47%, 55%)"
                    strokeWidth="1.5"
                    initial={{ opacity: 0 }}
                    animate={isVisible ? { opacity: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.6 }}
                />

                {/* Specialist polygon */}
                <motion.polygon
                    points={specialistPoints}
                    fill="hsl(43, 74%, 53%)"
                    fillOpacity="0.2"
                    stroke="hsl(43, 74%, 53%)"
                    strokeWidth="2"
                    initial={{ opacity: 0 }}
                    animate={isVisible ? { opacity: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    style={{ filter: 'drop-shadow(0 0 6px hsl(43, 74%, 53%, 0.4))' }}
                />

                {/* Specialist dots */}
                {axes.map((axis, i) => {
                    const p = getPoint(i, axis.specialist);
                    return (
                        <motion.circle
                            key={`sp-${i}`}
                            cx={p.x} cy={p.y} r="4"
                            fill="hsl(43, 74%, 53%)"
                            stroke="white" strokeWidth="2"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={isVisible ? { scale: 1, opacity: 1 } : {}}
                            transition={{ duration: 0.3, delay: 0.4 + i * 0.1 }}
                            style={{ filter: 'drop-shadow(0 0 4px hsl(43, 74%, 53%, 0.6))' }}
                        />
                    );
                })}

                {/* Generalist dots */}
                {axes.map((axis, i) => {
                    const p = getPoint(i, axis.generalist);
                    return (
                        <motion.circle
                            key={`gen-${i}`}
                            cx={p.x} cy={p.y} r="3"
                            fill="hsl(222, 47%, 55%)"
                            stroke="white" strokeWidth="1.5"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={isVisible ? { scale: 1, opacity: 1 } : {}}
                            transition={{ duration: 0.3, delay: 0.7 + i * 0.1 }}
                        />
                    );
                })}

                {/* Labels */}
                {axes.map((axis, i) => {
                    const labelDist = maxRadius + 28;
                    const angle = (i * angleStep) - Math.PI / 2;
                    const lx = cx + labelDist * Math.cos(angle);
                    const ly = cy + labelDist * Math.sin(angle);
                    const lines = axis.label.split('\n');

                    return (
                        <text
                            key={`label-${i}`}
                            x={lx} y={ly}
                            textAnchor="middle"
                            dominantBaseline="central"
                            className="fill-muted-foreground"
                            fontSize="10"
                            fontFamily="inherit"
                        >
                            {lines.map((line, li) => (
                                <tspan key={li} x={lx} dy={li === 0 ? 0 : 12}>{line}</tspan>
                            ))}
                        </text>
                    );
                })}
            </svg>

            {/* Legend */}
            <div className="flex items-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-2 rounded-sm bg-gold" style={{ boxShadow: '0 0 6px hsl(43, 74%, 53%, 0.5)' }} />
                    <span className="text-xs text-muted-foreground">Com Despachante Especializado</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-2 rounded-sm" style={{ backgroundColor: 'hsl(222, 47%, 55%)' }} />
                    <span className="text-xs text-muted-foreground">Por Conta Própria</span>
                </div>
            </div>
        </div>
    );
};

// --- Animated Counter ---
const useCountUp = (end: number, duration: number, isVisible: boolean, prefix = '', suffix = '') => {
    const [count, setCount] = useState(0);
    const frameRef = useRef<number>();

    useEffect(() => {
        if (!isVisible) return;
        const startTime = performance.now();
        const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * end));
            if (progress < 1) {
                frameRef.current = requestAnimationFrame(animate);
            }
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
    }, [end, duration, isVisible]);

    return prefix + count + suffix;
};

// --- Stat Card ---
const StatCard: React.FC<{
    title: string;
    value: number;
    prefix?: string;
    suffix?: string;
    description: string;
    variant: 'gradient' | 'danger' | 'neutral';
    isVisible: boolean;
    delay: number;
}> = ({ title, value, prefix = '', suffix = '', description, variant, isVisible, delay }) => {
    const displayValue = useCountUp(value, 1.5, isVisible, prefix, suffix);
    const { openPopup } = useWhatsAppPopup();

    const variants = {
        gradient: 'bg-gradient-to-br from-gold/90 to-gold text-primary-foreground',
        danger: 'bg-white/10 backdrop-blur-sm border border-white/10',
        neutral: 'bg-white/10 backdrop-blur-sm border border-white/10',
    };

    const textColor = {
        gradient: 'text-primary',
        danger: 'text-red-400',
        neutral: 'text-primary-foreground',
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay }}
            className={`rounded-xl p-6 cursor-default transition-all duration-300 h-full flex flex-col ${variants[variant]}`}
            style={{ boxShadow: '0 4px 20px -5px hsl(0 0% 0% / 0.15)' }}
        >
            <p className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-2 ${variant === 'gradient' ? 'text-primary/70' : 'text-primary-foreground/60'
                }`}>
                {title}
            </p>
            <p
                className={`text-3xl sm:text-5xl font-bold mb-2 ${textColor[variant]}`}
                style={{
                    textShadow: variant !== 'gradient' && isVisible
                        ? `0 0 20px ${variant === 'danger' ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.2)'}`
                        : 'none',
                }}
            >
                {displayValue}
            </p>
            <p className={`text-sm leading-relaxed ${variant === 'gradient' ? 'text-primary/60' : 'text-primary-foreground/70'
                }`}>
                {description}
            </p>
            {variant === 'danger' && (
                <div className="mt-4">
                    <button
                        onClick={() => openPopup("despachante")}
                        className="text-xs font-bold text-red-400 hover:text-red-300 flex items-center gap-1 group/link"
                    >
                        EVITAR MULTA AGORA
                        <span className="group-hover/link:translate-x-1 transition-transform">→</span>
                    </button>
                </div>
            )}
        </motion.div>
    );
};

// --- Advantages List ---
const advantages = [
    { title: 'Conhecimento da Capitania', desc: 'Sabemos exatamente os procedimentos e exigências de cada Capitania dos Portos da região.' },
    { title: 'Zero Retrabalho', desc: 'Documentação feita certa da primeira vez, sem idas e vindas desnecessárias à Capitania.' },
    { title: 'Controle de Prazos', desc: 'Monitoramos vencimentos de TIE, CHA e seguros, avisando com antecedência para evitar multas.' },
];

// --- Main Section ---
const AnalisePerformanceSection: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const { openPopup } = useWhatsAppPopup();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.15 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-0 relative overflow-hidden">
            {/* Dark Header */}
            <div className="bg-primary py-12 px-6">
                <div className="container mx-auto max-w-6xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="inline-block px-4 py-1.5 rounded-full bg-gold/20 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                            Valor Estratégico
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary-foreground mb-4">
                            Por que contratar um despachante náutico?
                        </h2>
                        <p className="text-lg text-primary-foreground/70 max-w-3xl">
                            Muitos proprietários tentam resolver a documentação por conta própria, mas o <strong className="text-gold">Despachante Náutico Especializado</strong> evita erros, multas e dor de cabeça, garantindo um processo rápido e sem surpresas.
                        </p>

                        {/* Alert Cards Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-4xl">
                            <div className="relative flex flex-col">
                                <div className="absolute -top-2 -right-2 z-20 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse shadow-lg">
                                    ATENÇÃO
                                </div>
                                <StatCard
                                    title="Multa por Atraso"
                                    value={500}
                                    prefix="R$ "
                                    suffix="+"
                                    description="Valor médio de multa por não transferir a documentação no prazo legal."
                                    variant="danger"
                                    isVisible={isVisible}
                                    delay={0.4}
                                />
                            </div>
                            <div className="relative flex flex-col">
                                <div className="absolute -top-2 -right-2 z-20 bg-gold text-primary text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg">
                                    PRAZO FATAL
                                </div>
                                <StatCard
                                    title="Prazo Legal"
                                    value={60}
                                    suffix=" dias"
                                    description="Prazo máximo para transferência de propriedade após a compra da embarcação."
                                    variant="neutral"
                                    isVisible={isVisible}
                                    delay={0.5}
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Content */}
            <div className="bg-background py-16 px-6">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left - Text */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h3 className="text-2xl font-heading font-bold text-primary mb-4">
                                Análise de Performance
                            </h3>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                Comparativo entre fazer a documentação por conta própria e contratar um despachante náutico especializado. O profissional se destaca na velocidade de resolução e prevenção de problemas.
                            </p>

                            <ul className="space-y-5">
                                {advantages.map((adv, i) => (
                                    <motion.li
                                        key={adv.title}
                                        initial={{ opacity: 0, x: -15 }}
                                        animate={isVisible ? { opacity: 1, x: 0 } : {}}
                                        transition={{ duration: 0.4, delay: 0.3 + i * 0.15 }}
                                        className="flex items-start gap-3"
                                    >
                                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <strong className="text-primary">{adv.title}:</strong>
                                            <span className="text-muted-foreground ml-1">{adv.desc}</span>
                                        </div>
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Right - Radar Chart */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="bg-primary/[0.03] rounded-2xl p-6 sm:p-8 border border-border"
                            style={{ boxShadow: '0 4px 30px -4px hsl(0 0% 0% / 0.06)' }}
                        >
                            <RadarChart isVisible={isVisible} />
                        </motion.div>
                    </div>


                    {/* Performance CTA Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="mt-20 p-8 rounded-2xl bg-gradient-to-r from-primary to-primary/90 text-primary-foreground relative overflow-hidden shadow-2xl"
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
                        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="text-center md:text-left">
                                <h4 className="text-2xl font-bold mb-2">Não troque sua paz por burocracia</h4>
                                <p className="text-primary-foreground/70 max-w-xl">
                                    Enquanto nós resolvemos tudo na Capitania, você aproveita o seu tempo no mar. Economize semanas de idas e vindas desnecessárias.
                                </p>
                            </div>
                            <button onClick={() => openPopup("despachante")} className="flex-shrink-0 bg-gold text-primary font-bold px-8 py-4 rounded-lg hover:bg-gold/90 transition-all shadow-lg hover:shadow-gold/20 transform hover:-translate-y-1">
                                Acelerar meu Processo
                            </button>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default AnalisePerformanceSection;
